/**
 * CadenceMinimalPure
 *
 * Compact "minimal" rendering of a contact's cadence — the campaign's
 * remaining steps as a single horizontal, wrapping row of step-icon chips
 * with short delay pills between days. It's the low-density counterpart to
 * `CadenceSectionPure`'s full step cards, toggled by the Minimal/Expanded
 * switch on the Interact tab. Mirrors the switch-graph's
 * `DestinationCampaignMinimal`, but reads the CONTACT's live cadence graph
 * (with per-step state) instead of a template.
 *
 * Display-only: no edit affordances. To match the expanded view, resolved
 * steps (finished / skipped / canceled / contact_unavailable) are hidden. At a
 * branch (wait_for_connect / sift) the walk follows the branch this contact
 * actually took (`selectedOption`) once resolved — mirroring the expanded view,
 * which renders only `next[selectedOption]`. While a branch is still pending it
 * follows the Yes / main spine (`next.one`), the same primary path the expanded
 * card leads with; the not-yet-taken sibling branch isn't inlined in the
 * compact row.
 */
import { Fragment, useMemo } from 'react';

import type { CadenceStepNodePure, CadenceStepStatePure, CadenceStepTypePure } from '../CGStep';
import type { CadenceGraph } from './CadenceSectionPure';
import { cn } from '../cn';
import { StepIcon } from '../StepIcon';
import { Arrows, Time } from '../StrokeIcons';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

/** Short delay label for a between-days pill ("1w", "3d", "6h"). */
function shortDelay(ms: number): string {
  if (ms <= 0) return '';
  if (ms % WEEK_MS === 0) return `${ms / WEEK_MS}w`;
  const days = Math.round(ms / DAY_MS);
  if (days >= 1) return `${days}d`;
  return `${Math.max(1, Math.round(ms / HOUR_MS))}h`;
}

const STEP_LABEL: Record<CadenceStepTypePure, string> = {
  message: 'Message',
  connect: 'Connect',
  inmail: 'InMail',
  visit: 'Visit profile',
  follow: 'Follow',
  email: 'Email',
  reminder: 'Reminder',
  call: 'Call',
  delay: 'Delay',
  likeRecentPosts: 'Like posts',
  wait_for_connect: 'Wait for connection',
  voice_note: 'Voice note',
  sift: 'Filter',
  switch_campaign: 'Change to campaign',
  switch: 'Switch',
};

// Resolved steps are hidden — they'd mislead the reader into thinking the
// cadence will still execute them. Mirrors CGDelayStepPure's day-card filter.
const RESOLVED_STATES: ReadonlySet<CadenceStepStatePure> = new Set([
  'finished',
  'skipped',
  'canceled',
  'contact_unavailable',
]);

type MinimalStep = {
  id: string;
  type: CadenceStepTypePure;
  state: CadenceStepStatePure;
  /** Wait before this step (ms). Only rendered as a pill between days. */
  delayBeforeMs: number;
};

/**
 * The next node to walk to. At a resolved branch we follow the branch this
 * contact took (`next[selectedOption]`) — matching the expanded view, which
 * renders only that branch. Otherwise (linear nodes, or a still-pending
 * branch) we follow the Yes / main spine, falling back to the No branch.
 */
function nextCursor(node: CadenceStepNodePure): string | null {
  const nextId = node.selectedOption != null ? node.next[node.selectedOption] : (node.next.one ?? node.next.two);
  return nextId ?? null;
}

/**
 * Walk the cadence graph into a flat, ordered list of upcoming steps.
 *
 * The graph links days in two styles (see `cadenceStepBuildGraph`): a
 * `delay` node holds its day's steps in `group` and points to the next day
 * via `next.one`; branch/root leaves chain directly via `next.one`. We
 * handle both, carrying each day's leading wait onto its first step. When a
 * whole day (or step) is hidden because every step in it is resolved, its
 * wait is carried forward onto the next visible step (`pendingDelayMs`) so
 * the between-days pill reflects the true elapsed time rather than under-
 * reporting it. A `seen` set guards against malformed cycles.
 */
export function buildMinimalSteps(graph: CadenceGraph | null | undefined): MinimalStep[] {
  if (graph?.nodes == null || graph.startId == null) return [];
  const { nodes, startId } = graph;
  const out: MinimalStep[] = [];
  const seen = new Set<string>();
  let cursor: string | null | undefined = startId;
  // Wait accrued from fully-resolved (hidden) days/steps, applied to the next
  // step that actually renders.
  let pendingDelayMs = 0;

  while (cursor != null && !seen.has(cursor)) {
    seen.add(cursor);
    const node: CadenceStepNodePure | undefined = nodes[cursor];
    if (node == null) break;

    if (node.type === 'delay') {
      // A day: its steps live in `group` (ordered); the delay is the wait
      // before the day, so it attaches to the first visible step only.
      let firstOfDay = true;
      for (const id of node.group) {
        const step = nodes[id];
        if (step == null || RESOLVED_STATES.has(step.state)) continue;
        out.push({
          id: step.id,
          type: step.type,
          state: step.state,
          delayBeforeMs: firstOfDay ? pendingDelayMs + node.delay : 0,
        });
        if (firstOfDay) pendingDelayMs = 0;
        firstOfDay = false;
      }
      // Whole day hidden (no visible step) → carry its wait forward.
      if (firstOfDay) pendingDelayMs += node.delay;
      cursor = nextCursor(node);
    } else {
      // A leaf step (message/connect/…), a `sift`/`wait_for_connect`
      // branch node, or a bare-leaf root / branch-chain member.
      if (!RESOLVED_STATES.has(node.state)) {
        out.push({
          id: node.id,
          type: node.type,
          state: node.state,
          delayBeforeMs: pendingDelayMs + node.delay,
        });
        pendingDelayMs = 0;
      } else {
        pendingDelayMs += node.delay;
      }
      cursor = nextCursor(node);
    }
  }

  return out;
}

function MinimalStepChip({ step }: { step: MinimalStep }) {
  // wait_for_connect has no step glyph (it's a conditional wait) — render a
  // dashed hourglass chip so it reads as "pause until connected".
  if (step.type === 'wait_for_connect') {
    return (
      <span
        title={STEP_LABEL.wait_for_connect}
        aria-label={STEP_LABEL.wait_for_connect}
        data-testid="cadence-minimal-wait"
        className="flex size-5 shrink-0 items-center justify-center rounded-sm border border-dashed border-slate-300 text-slate-500 dark:border-slate-600 dark:text-slate-400">
        <Time.ClockRewind className="size-3 stroke-2" />
      </span>
    );
  }
  return (
    <StepIcon
      iconType={step.type}
      state={step.state}
      title={STEP_LABEL[step.type]}
      aria-label={STEP_LABEL[step.type]}
      data-testid="cadence-minimal-step"
    />
  );
}

export function CadenceMinimalPure({
  stepsGraph,
  className,
}: {
  stepsGraph: CadenceGraph | null | undefined;
  className?: string;
}) {
  const steps = useMemo(() => buildMinimalSteps(stepsGraph), [stepsGraph]);

  if (steps.length === 0) {
    return (
      <div
        data-testid="cadence-minimal-empty"
        className={cn(
          'flex items-center justify-center px-3 py-6 text-sm italic text-lynch-400 dark:text-lynch-500',
          className,
        )}>
        No upcoming steps
      </div>
    );
  }

  return (
    <div data-testid="cadence-minimal" className={cn('flex flex-wrap items-center gap-1.5 px-3 py-3', className)}>
      {steps.map((step, i) => (
        <Fragment key={step.id}>
          {i > 0 && step.delayBeforeMs > 0 && (
            <span
              title={`${shortDelay(step.delayBeforeMs)} wait`}
              data-testid="cadence-minimal-delay"
              className="flex items-center gap-0.5 rounded-md bg-lynch-50 px-1.5 py-0.5 text-[10px] font-medium text-lynch-500 dark:bg-slate-800 dark:text-lynch-400">
              <Time.Clock className="size-3" />
              {shortDelay(step.delayBeforeMs)}
            </span>
          )}
          {i > 0 && step.delayBeforeMs <= 0 && (
            <Arrows.ChevronRight aria-hidden className="size-3.5 shrink-0 text-lynch-300 dark:text-lynch-600" />
          )}
          <MinimalStepChip step={step} />
        </Fragment>
      ))}
    </div>
  );
}

export default CadenceMinimalPure;
